import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/shared/auth/store';
import { matchesQuery } from '@/shared/lib/strings';
import type { CommandItem } from '@/shared/registry/types';

import { useActivityCounters, useCrmMe, type EntityType } from '../api';
import { useOpenNewRecord } from '../hooks';

/** Sidebar badge of "CRM": the caller's overdue activities (refreshed by realtime events). */
export function useCrmNavCounter(): number | undefined {
  const signedIn = useAuthStore((s) => s.status === 'authenticated');
  const me = useCrmMe({ enabled: signedIn });
  const counters = useActivityCounters({ enabled: signedIn && !!me.data && me.data.role !== 'none' });
  const overdue = counters.data?.overdue ?? 0;
  return overdue > 0 ? overdue : undefined;
}

/** Whether the lead card and the lead forms are offered: false once «use leads» is switched off (`sidePanels.lead.useAvailable`). */
export function useLeadsAvailable(): boolean {
  const signedIn = useAuthStore((s) => s.status === 'authenticated');
  const me = useCrmMe({ enabled: signedIn });
  return me.data?.useLeads !== false;
}

/** ⌘K: "New deal / lead / contact / company / quote / invoice" while the query is empty or names them (search hits come from the search module). */
export function useCrmCommands(query: string): { items: CommandItem[] } {
  const { t } = useTranslation('crm');
  const openNew = useOpenNewRecord();
  const signedIn = useAuthStore((s) => s.status === 'authenticated');
  const me = useCrmMe({ enabled: signedIn });
  const data = me.data;
  if (!data || data.role === 'none') return { items: [] };
  const items: CommandItem[] = (['deal', 'lead', 'contact', 'company', 'quote', 'invoice'] as EntityType[])
    .filter((type) => data.access[type].canCreate && (type !== 'lead' || data.useLeads !== false))
    .map((type) => ({
      id: `crm:new-${type}`,
      label: t(`actions.new.${type}`),
      icon: PlusIcon,
      keywords: [t('page.title'), 'crm', 'црм', t(`entities.${type}`)],
      perform: () => openNew(type),
    }));

  // «Свои сущности»: "New contract", "New repair request"… for each type the caller may create items of.
  for (const entry of data.dynamicTypes ?? []) {
    if (!entry.access.canCreate) continue;
    const name = entry.type.itemTitle.toLocaleLowerCase();
    items.push({
      id: `crm:new-item-${entry.type.id}`,
      label: t('items.new', { name }),
      icon: PlusIcon,
      keywords: [t('page.title'), 'crm', 'црм', entry.type.title, entry.type.itemTitle, entry.type.code],
      perform: () => openNew('item', { crmType: entry.type.id }),
    });
  }

  return { items: items.filter((item) => matchesQuery(query, item.label, ...(item.keywords ?? []))) };
}
